import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Debounced autosave. `schedule(value)` records the latest value and, after
// `delay` ms of quiet, runs `save(value)`. Only the most recent value is ever
// sent (latest-wins); a save in flight is followed by one more if newer edits
// arrived. Exposes a coarse status for a "Saving… / Saved" indicator.
export function useAutosave<T>(save: (v: T) => Promise<unknown>, delay = 800): {
  status: SaveStatus;
  schedule: (v: T) => void;
} {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<{ v: T } | null>(null);
  const inFlight = useRef(false);
  const dirty = useRef(false);
  const saveRef = useRef(save);
  saveRef.current = save;

  const run = useCallback(async () => {
    if (inFlight.current || !latest.current) return;
    inFlight.current = true;
    dirty.current = false;
    setStatus('saving');
    try {
      await saveRef.current(latest.current.v);
      setStatus(dirty.current ? 'saving' : 'saved');
    } catch {
      setStatus('error');
    } finally {
      inFlight.current = false;
      if (dirty.current) void run(); // newer edits arrived while saving
    }
  }, []);

  const schedule = useCallback(
    (v: T) => {
      latest.current = { v };
      dirty.current = true;
      setStatus('saving');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void run(), delay);
    },
    [delay, run],
  );

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return { status, schedule };
}
